import { useEffect, useRef, useState } from 'react';

interface Subscribable<T> {
  subscribe: (next: (value: T) => void) => { unsubscribe: () => void };
}

/**
 * Like `useObservable`, but also reports whether the first emission has arrived.
 *
 * `loading` is `true` until the current subscription emits once, and flips back
 * to `true` whenever `deps` change (a new query re-shows its skeleton). This lets
 * a screen distinguish "still loading" from "loaded but empty" — WatermelonDB's
 * `.observe()` otherwise just yields `[]` on both.
 */
export function useObservableStatus<T>(
  factory: () => Subscribable<T> | null,
  deps: ReadonlyArray<unknown>,
  initial: T,
): { data: T; loading: boolean } {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  // Track the active subscription so a late emission from a stale query is ignored.
  const runId = useRef(0);

  useEffect(() => {
    const id = ++runId.current;
    setLoading(true);
    const obs = factory();
    if (!obs) {
      setLoading(false);
      return;
    }
    const sub = obs.subscribe((value) => {
      if (runId.current !== id) return;
      setData(value);
      setLoading(false);
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading };
}
